import { zodResolver } from '@hookform/resolvers/zod'
import type React from 'react'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLinkLegacy } from '@/components/ui/button-link-legacy'
import { Container } from '@/components/ui/container'

import { AirbnbIcon, CheckIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useResetPassword } from '@/features/auth/queries/use-reset-password'
import {
	RESET_PASSWORD_FORM_MODEL,
	ResetPasswordFormSchema,
	type ResetPasswordValidationSchema
} from '@/features/auth/reset-password-form-model'
import { errorHandler } from '@/helpers/utils'
import { routes } from '@/routes'
import { type TKey, useTranslation } from '@/translations/i18n'

export const ResetPassword: React.FC = () => {
	const { t } = useTranslation()
	const [resetSuccess, setResetSuccess] = useState(false)
	const {
		register,
		handleSubmit,
		formState: { errors }
	} = useForm({
		mode: 'onBlur',
		resolver: zodResolver(ResetPasswordFormSchema)
	})

	//reset password mutation
	const { mutate: resetPassword, isPending } = useResetPassword({
		onError: (e) => errorHandler(e), //toast.error(t(e.message)),
		onSuccess: () => {
			setResetSuccess(true)
		}
	})

	const onSubmit: SubmitHandler<ResetPasswordValidationSchema> = (data) => {
		resetPassword({ email: data.email })
	}

	return (
		<Container>
			<title>{t('set_password:page_title_meta')}</title>
			<div className='flex h-full items-center justify-center'>
				<div className='flex w-[360px] flex-col items-stretch'>
					{resetSuccess && (
						<>
							<div className='flex justify-center'>
								<CheckIcon
									className='fill-success-600'
									size={40}
								/>
							</div>
							<h4 className='mt-6 text-center font-bold text-display-sm text-neutral-900'>
								{t('reset_password:success_title')}
							</h4>
							<p className='my-2 mb-14 text-center text-md'>
								{t('reset_password:success_message')}
							</p>
							<ButtonLinkLegacy
								size={'md'}
								to={routes.LOGIN.path}
								type={'submit'}
								variant={'primary'}
							>
								{t('reset_password:success_cta')}
							</ButtonLinkLegacy>
						</>
					)}
					{!resetSuccess && (
						<>
							<div className='flex justify-center'>
								<AirbnbIcon
									className='fill-primary-600'
									size={40}
								/>
							</div>
							<h4 className='mt-6 text-center font-bold text-display-sm text-neutral-900'>
								{t('reset_password:title')}
							</h4>
							<p className='my-2 mb-14 text-center text-md'>
								{t('reset_password:subtitle')}
							</p>
							<form
								className='flex flex-col items-stretch gap-4'
								onSubmit={handleSubmit(onSubmit)}
							>
								<div className='flex flex-col gap-4'>
									<InputText
										errorMessage={t(
											(errors.email?.message ||
												'') as TKey
										)}
										label={t(
											RESET_PASSWORD_FORM_MODEL.email
												.label
										)}
										placeholder={t(
											RESET_PASSWORD_FORM_MODEL.email
												.placeholder
										)}
										type={'email'}
										{...register(
											RESET_PASSWORD_FORM_MODEL.email.name
										)}
									/>
								</div>

								<div className='flex flex-col items-stretch gap-2'>
									<ButtonBrandLegacy
										disabled={isPending}
										size={'md'}
										type={'submit'}
									>
										{t('reset_password:primary_cta')}
										{isPending && <Spinner size={16} />}
									</ButtonBrandLegacy>
									<ButtonLinkLegacy
										size={'md'}
										to={routes.LOGIN.path}
										type={'submit'}
										variant={'tertiary'}
									>
										{t('reset_password:success_cta')}
									</ButtonLinkLegacy>
								</div>
							</form>
						</>
					)}
				</div>
			</div>
		</Container>
	)
}

ResetPassword.displayName = 'SetPassword'
